import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useDisclosure
} from '@chakra-ui/react'
import { FaBitcoin, FaChartLine, FaCode, FaHistory } from 'react-icons/fa'
import Header from '../Header'

import IconWrapper from './components/IconWrapper'
import LoadingState from './components/LoadingState'
import ErrorState from './components/ErrorState'
import CoinHeader from './components/CoinHeader'
import MarketDataPanel from './components/MarketDataPanel'
import SupplyPanel from './components/SupplyPanel'
import DevelopmentPanel from './components/DevelopmentPanel'
import HistoryPanel from './components/HistoryPanel'

import { CoinData, CryptoTickerProps } from './types'
import { useCryptoTickerStyles } from './styles'
import { fetchCryptoData } from './utils'

const CryptoTicker = ({ coinId, title, subtitle, icon = FaBitcoin }: CryptoTickerProps): JSX.Element => {
  const [data, setData] = useState<CoinData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const { isOpen: isDescriptionOpen, onToggle: onDescriptionToggle } = useDisclosure()
  const descriptionRef = useRef<HTMLDivElement>(null)

  // Get styles
  const { gridTemplateColumns, cardBg, statCardBg, borderColor, accentColor } = useCryptoTickerStyles()

  const handleFetchData = async () => {
    await fetchCryptoData(coinId, setData, setError, setLoading, setRefreshing)
  }

  useEffect(() => {
    handleFetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId])

  if (loading && !data) {
    return <LoadingState title={title} subtitle={subtitle} />
  }

  if (error) {
    return (
      <ErrorState
        title={title}
        subtitle={subtitle}
        error={error}
        onRetry={handleFetchData}
        isRetrying={refreshing}
      />
    )
  }

  if (!data) return <Box p={{ base: 2, md: 4 }}><Header title={title} subtitle={subtitle} /></Box>

  return (
    <Box p={{ base: 2, md: 4 }}>
      <Header title={title} subtitle={subtitle} />

      {/* Price Header Section */}
      <CoinHeader
        data={data}
        onRefresh={handleFetchData}
        isRefreshing={refreshing}
        cardBg={cardBg}
        borderColor={borderColor}
      />

      {/* Main Content Tabs */}
      <Tabs variant="enclosed" colorScheme="blue" isLazy>
        <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap">
          <Tab><IconWrapper icon={FaChartLine} mr={2} /> Market Data</Tab>
          <Tab><IconWrapper icon={icon} mr={2} /> Supply</Tab>
          <Tab><IconWrapper icon={FaCode} mr={2} /> Development</Tab>
          <Tab><IconWrapper icon={FaHistory} mr={2} /> History</Tab>
        </TabList>

        <TabPanels>
          {/* Market Data Panel */}
          <TabPanel>
            <MarketDataPanel
              data={data}
              gridTemplateColumns={gridTemplateColumns}
              statCardBg={statCardBg}
            />
          </TabPanel>

          {/* Supply Panel */}
          <TabPanel>
            <SupplyPanel
              data={data}
              gridTemplateColumns={gridTemplateColumns}
              statCardBg={statCardBg}
            />
          </TabPanel>

          {/* Development Panel */}
          <TabPanel>
            <DevelopmentPanel
              data={data}
              gridTemplateColumns={gridTemplateColumns}
              statCardBg={statCardBg}
            />
          </TabPanel>

          {/* History Panel */}
          <TabPanel>
            <HistoryPanel
              data={data}
              gridTemplateColumns={gridTemplateColumns}
              statCardBg={statCardBg}
              cardBg={cardBg}
              accentColor={accentColor}
              isDescriptionOpen={isDescriptionOpen}
              onDescriptionToggle={onDescriptionToggle}
              descriptionRef={descriptionRef}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}

export default CryptoTicker
